import { Button } from '../ui/catalyst/button';
import { Text } from '../ui/catalyst/text';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading?: boolean;
  pageSizeOptions?: number[];
}

/**
 * Reusable pagination controls using Catalyst UI components.
 *
 * Features:
 * - Page navigation (prev/next, page numbers)
 * - Page size selector
 * - Results count display
 * - Loading state support
 * - Responsive design
 *
 * Used by:
 * - Intakes list
 * - Matters list (future)
 * - Any other paginated table
 */
export const PaginationControls = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  pageSizeOptions = [25, 50, 100, 200],
}: PaginationControlsProps) => {
  // Generate page numbers to show (max 7 pages visible)
  const getPageNumbers = () => {
    const pages: (number | 'gap')[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Show pages around current page
      if (currentPage > 3) {
        pages.push('gap');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('gap');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-between px-3 py-1.5 border-t border-zinc-300 dark:border-zinc-700">
      {/* Page navigation (left side) */}
      <nav className="flex gap-x-1 items-center" aria-label="Page navigation">
        {/* Previous button */}
        <Button
          plain
          disabled={currentPage === 1 || isLoading}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          className="!text-xs !h-7 !px-2 gap-1"
        >
          <svg
            className="stroke-current h-3.5 w-3.5"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Previous
        </Button>

        {/* Page numbers */}
        <span className="hidden items-baseline gap-x-1 sm:flex">
          {getPageNumbers().map((page, idx) =>
            page === 'gap' ? (
              <span
                key={`gap-${idx}`}
                aria-hidden="true"
                className="w-6 select-none text-center text-xs font-semibold text-zinc-950 dark:text-white"
              >
                &hellip;
              </span>
            ) : (
              <Button
                key={page}
                plain
                disabled={isLoading}
                onClick={() => onPageChange(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
                className={
                  page === currentPage
                    ? 'min-w-[1.75rem] !h-7 !px-1 !text-xs before:absolute before:-inset-px before:rounded-md before:bg-zinc-950/5 dark:before:bg-white/10'
                    : 'min-w-[1.75rem] !h-7 !px-1 !text-xs'
                }
              >
                <span className="-mx-0.5">{page}</span>
              </Button>
            ),
          )}
        </span>

        {/* Next button */}
        <Button
          plain
          disabled={currentPage === totalPages || isLoading}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          className="!text-xs !h-7 !px-2 gap-1"
        >
          Next
          <svg
            className="stroke-current h-3.5 w-3.5"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </nav>

      {/* Page size selector (right side) */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Text className="!text-xs text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
          Per page:
        </Text>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          disabled={isLoading}
          className="h-7 w-16 rounded-md border border-zinc-950/10 dark:border-white/10 bg-transparent dark:bg-white/5 px-2 pr-7 text-xs text-zinc-950 dark:text-white appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20stroke%3D%22%23888%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M5.75%2010.75L8%2013l2.25-2.25M10.25%205.25L8%203%205.75%205.25%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1rem] bg-[right_0.25rem_center] bg-no-repeat focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
